// Closing-only read-only slide-out summarizing a potential Cost to Cure: the
// figures vs the last disclosed LE/GFE, the invoices on file, and the
// processor's escalation explanation. The cure itself happens at closing, not
// here. Ported from FeeTable.jsx CostToCureSheet.
import { useEffect } from 'react';
import { Icon } from '../atoms';
import { fmtMoney, fmtStamp } from '../format';
import { costToCureCheck, invoiceTotal, suggestInvoiceFolder, type DocOrderFee } from '../core';

interface CostToCureSheetProps {
  fee: DocOrderFee;
  onClose: () => void;
}

export function CostToCureSheet({ fee, onClose }: CostToCureSheetProps) {
  const disclosed = fee.lastDisclosedLE !== null ? Number(fee.lastDisclosedLE) : 0;
  const invTotal = invoiceTotal(fee);
  const amount = Number(fee.amount) || 0;
  const { costToCure } = costToCureCheck(fee);
  const M = (n: number) => fmtMoney(n, true);
  // R5 (review-2 #2 + Codex R5-a finding 5): ONLY a typed cost_to_cure
  // escalation may render as the CTC explanation. Kind-less legacy records
  // can originate from EITHER old flow, so neither subtype label is
  // defensible — they render only in the neutral EscalationSheet.
  const rawEscalation = fee.acks?.escalation;
  const escalation = rawEscalation?.kind === 'cost_to_cure' ? rawEscalation : undefined;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const roleLabel = escalation?.role === 'closing' ? 'Closing' : escalation?.role === 'lo' ? 'LO' : escalation?.role === 'processor' ? 'Processor' : null;
  const stampParts = [escalation?.by, roleLabel, escalation?.at ? fmtStamp(escalation.at) : null].filter(Boolean);

  return (
    <div className="recon-overlay" data-doc-order>
      <div className="recon-scrim" onClick={onClose} />
      <aside className="recon-sheet" role="dialog" aria-label={`Cost to Cure — ${fee.name}`}>
        <header className="recon-hd">
          <div>
            <div className="recon-eyebrow ctc">
              <Icon name="alertTriangle" size={12} />Potential Cost to Cure
            </div>
            <div className="recon-title">
              {fee.name}
              {fee.payee ? <span className="recon-payee"> · {fee.payee}</span> : null}
            </div>
          </div>
          <button className="recon-close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={15} />
          </button>
        </header>

        <div className="recon-compare">
          <div className="recon-cmp-row">
            <span>Actual fee entered</span>
            <b>{M(amount)}</b>
          </div>
          <div className="recon-cmp-row">
            <span>
              Last disclosed fee <span className="recon-cmp-src">LE / GFE</span>
            </span>
            <b>{M(disclosed)}</b>
          </div>
          <div className="recon-cmp-row">
            <span>Invoice total</span>
            <b>{M(invTotal)}</b>
          </div>
          <div className="recon-cmp-row delta over">
            <span>Cost to Cure</span>
            <b>{M(costToCure)} under disclosed</b>
          </div>
        </div>

        <div className="ctc-sheet-block">
          <span className="fee-ctc-lbl">Invoice{(fee.invoices ?? []).length > 1 ? 's' : ''}</span>
          {(fee.invoices ?? []).length ? (
            (fee.invoices ?? []).map((inv, i) => (
              <div key={`${inv.uid}-${i}`} className="ctc-sheet-doc">
                <Icon name="fileText" size={14} />
                <span>{inv.file || inv.folder || suggestInvoiceFolder(fee.name, fee)}</span>
                <b>{M(Number(inv.amount) || 0)}</b>
              </div>
            ))
          ) : (
            <div className="vsum-empty">No invoice on file.</div>
          )}
        </div>

        <div className="ctc-sheet-block">
          <span className="fee-ctc-lbl">Processor&rsquo;s explanation{stampParts.length ? ` · ${stampParts.join(' · ')}` : ''}</span>
          {escalation?.note ? <p className="ctc-sheet-note">&ldquo;{escalation.note}&rdquo;</p> : <div className="vsum-empty">No explanation from the Processor on file.</div>}
        </div>

        <div className="recon-stamp">
          <Icon name="alertCircle" size={11} /> Informational — the Cost to Cure is reviewed and cured at closing, not on this screen.
        </div>
      </aside>
    </div>
  );
}
